import {
  Alert,
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import PersonAddIcon from "@mui/icons-material/PersonAddAlt1Outlined";
import EditIcon from "@mui/icons-material/EditOutlined";
import BlockIcon from "@mui/icons-material/BlockOutlined";
import VisibilityIcon from "@mui/icons-material/VisibilityOutlined";
import GavelIcon from "@mui/icons-material/GavelOutlined";
import LinkIcon from "@mui/icons-material/LinkOutlined";
import { useCallback, useEffect, useMemo, useState } from "react";
import type {
  CallContact,
  CallContactEndpoints,
  ResolvedCallPolicy,
} from "@shared/types/foyer";
import { foyerApi } from "../http/foyer";
import DangerConfirmDialog from "./DangerConfirmDialog";
import FamilyContactDialog, { CONSENT_CAPACITIES } from "./FamilyContactDialog";
import TableSkeleton from "./TableSkeleton";

/**
 * One resident's family contacts - "Anhöriga / videosamtal" (VC-11).
 *
 * It opens from the roster row, and straight after an admit, because that is
 * where staff already are: adding the daughter is part of admitting the mother,
 * not a second errand on a page nobody remembers to visit.
 *
 * What this list refuses to do is as important as what it shows:
 *
 *  - **No phone numbers on screen.** Rows carry the server's *mask*
 *    ("+46 70 ***** 67"), which is enough to tell two daughters apart and not
 *    enough to place a call. Pulling a real number is one click, one row per
 *    click in the access log - a list view that rendered plaintext would turn a
 *    page load into a dozen silent PHI disclosures.
 *  - **No activation without consent.** The direction checkboxes are inert on a
 *    row that has no consent record, and the row reads "Inactive · consent
 *    missing" rather than looking merely unticked.
 *  - **No pretending these are permanent.** The discharge notice is at the top,
 *    not in a tooltip: family contacts are revoked with the stay (VC-06), and
 *    staff who don't know that will build habits around an assumption we break.
 */

interface Props {
  open: boolean;
  premiseId: string;
  /** The resident (PhiRecord id) these contacts belong to. */
  personRef: string;
  /** Human label for the room, for the title. */
  roomLabel: string;
  /** Premise timezone, prefilled into quiet hours. */
  defaultTz: string;
  /** Revealing a number needs PHI access; without it the button is not offered. */
  canViewPhi: boolean;
  onClose: () => void;
  /** Fired after any mutation so the roster can refresh its per-resident count. */
  onChanged?: () => void;
}

function consentLabel(c: CallContact): string {
  if (!c.consent) return "Consent missing";
  const cap =
    CONSENT_CAPACITIES.find((o) => o.value === c.consent?.capacity)?.label ??
    c.consent.capacity;
  const when = new Date(c.consent.grantedAt);
  const date = Number.isNaN(when.getTime())
    ? c.consent.grantedAt
    : when.toLocaleDateString();
  return cap ? `${cap} · ${date}` : `Consent · ${date}`;
}

export default function FamilyContactsDialog({
  open,
  premiseId,
  personRef,
  roomLabel,
  defaultTz,
  canViewPhi,
  onClose,
  onChanged,
}: Props) {
  const [contacts, setContacts] = useState<CallContact[] | null>(null);
  const [policy, setPolicy] = useState<ResolvedCallPolicy | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busyRow, setBusyRow] = useState<string | null>(null);

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<CallContact | null>(null);
  const [revoking, setRevoking] = useState<CallContact | null>(null);
  const [revokeBusy, setRevokeBusy] = useState(false);
  const [revokeError, setRevokeError] = useState<string | null>(null);

  // Revealed endpoints, keyed by contact id. Each entry cost one audited
  // disclosure, so it is cached for the life of the dialog and dropped on close.
  const [revealed, setRevealed] = useState<
    Record<string, CallContactEndpoints>
  >({});
  const [revealing, setRevealing] = useState<string | null>(null);

  /**
   * The two links a relative can be handed (VC3-5, PORTAL-2), issued straight
   * from the row.
   *
   * They used to live only inside the edit form, one click and a scroll past the
   * consent section away - which is the same as not existing: the operator
   * standing at the admission meeting with the daughter in front of them looks
   * at THIS list. Handing over the link is the job; editing the row is not.
   *
   * The minted URL is shown ONCE, so it goes in a banner at the top of the
   * dialog rather than in a menu that closes over it.
   */
  const [linkMenu, setLinkMenu] = useState<{
    anchor: HTMLElement;
    contact: CallContact;
  } | null>(null);
  const [linkBusy, setLinkBusy] = useState<string | null>(null);
  const [issued, setIssued] = useState<{
    kind: "call" | "portal";
    contactName: string;
    url: string;
    emailed: boolean;
    rotated: boolean;
  } | null>(null);
  const [copied, setCopied] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const rows = await foyerApi.listCallContacts(premiseId, {
        scope: "person",
        personRef,
      });
      setContacts(rows);
      setError(null);
    } catch (err) {
      setContacts([]);
      setError(err instanceof Error ? err.message : String(err));
    }
  }, [premiseId, personRef]);

  useEffect(() => {
    if (!open) {
      // Nothing revealed survives the dialog closing - a decrypted number left
      // on screen behind a closed dialog is a disclosure nobody asked for twice.
      setRevealed({});
      setNotice(null);
      // Same rule for a minted link: it is a bearer credential for a resident's
      // camera, so it does not sit on screen after the dialog is done with.
      setIssued(null);
      setLinkMenu(null);
      return;
    }
    setContacts(null);
    void refresh();
  }, [open, refresh]);

  useEffect(() => {
    if (!open) return;
    foyerApi
      .getCallPolicy(premiseId)
      .then(setPolicy)
      .catch(() => setPolicy(null));
  }, [open, premiseId]);

  const sorted = useMemo(() => {
    const rows = contacts ?? [];
    return [...rows].sort(
      (a, b) =>
        Number(!a.consent) - Number(!b.consent) ||
        a.priority - b.priority ||
        a.displayName.localeCompare(b.displayName, "sv")
    );
  }, [contacts]);

  const afterMutation = async (msg: string) => {
    setNotice(msg);
    await refresh();
    onChanged?.();
  };

  /** Flip one direction straight from the row - never on an unconsented row. */
  const toggleFlag = async (
    c: CallContact,
    field: "callableFromRoom" | "canCallRoom",
    next: boolean
  ) => {
    setBusyRow(c.id);
    setError(null);
    try {
      await foyerApi.updateCallContact(premiseId, c.id, { [field]: next });
      await refresh();
      onChanged?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusyRow(null);
    }
  };

  const reveal = (c: CallContact) => {
    if (revealed[c.id] || revealing) return;
    setRevealing(c.id);
    setError(null);
    foyerApi
      .revealCallEndpoints(premiseId, c.id)
      .then((e) => setRevealed((prev) => ({ ...prev, [c.id]: e })))
      .catch((err) =>
        setError(err instanceof Error ? err.message : String(err))
      )
      .finally(() => setRevealing(null));
  };

  const hide = (id: string) =>
    setRevealed((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });

  /**
   * Mint one of the two links for a contact. Both are shown once and reported
   * honestly: `emailed` says whether staff still have to pass it on themselves,
   * and for the call link `rotated` says whether a link the family already holds
   * has just stopped working.
   */
  const issueLink = async (c: CallContact, kind: "call" | "portal") => {
    setLinkMenu(null);
    setLinkBusy(c.id);
    setError(null);
    setIssued(null);
    setCopied(false);
    try {
      if (kind === "call") {
        const res = await foyerApi.sendFamilyCallLink(premiseId, c.id);
        setIssued({
          kind,
          contactName: c.displayName,
          url: res.url,
          emailed: res.emailed,
          rotated: res.rotated,
        });
      } else {
        const res = await foyerApi.sendFamilyPortalInvite(premiseId, c.id);
        setIssued({
          kind,
          contactName: c.displayName,
          url: res.url,
          emailed: res.emailed,
          rotated: false,
        });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLinkBusy(null);
    }
  };

  /** Best-effort copy - the URL is on screen either way, this just saves typing. */
  const copyIssued = () => {
    if (!issued) return;
    void navigator.clipboard
      ?.writeText(issued.url)
      .then(() => setCopied(true))
      .catch(() => setCopied(false));
  };

  const confirmRevoke = async () => {
    if (!revoking) return;
    setRevokeBusy(true);
    setRevokeError(null);
    try {
      await foyerApi.revokeCallContact(premiseId, revoking.id);
      const name = revoking.displayName;
      setRevoking(null);
      await afterMutation(`${name} revoked`);
    } catch (err) {
      setRevokeError(err instanceof Error ? err.message : String(err));
    } finally {
      setRevokeBusy(false);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="md"
        data-testid="family-contacts-dialog"
      >
        <DialogTitle sx={{ pb: 0.5 }}>
          Family &amp; video calls
          <Typography variant="caption" color="text.secondary" display="block">
            {roomLabel}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {/* VC-06 lifecycle, stated up front rather than discovered later. */}
            <Alert severity="info" data-testid="family-discharge-notice">
              These contacts belong to the <strong>resident</strong>, not to the
              room. They disappear from this room’s TV the moment the resident
              is discharged, and are revoked with the stay - the next person in
              this room never sees them. Re-admitting means adding them again.
            </Alert>

            {policy && !policy.enabled && (
              <Alert severity="warning" data-testid="family-calls-disabled">
                Video calling is switched off for this premise, so nothing here
                reaches a TV yet. Having the video calling product doesn’t turn
                this on by itself - it’s a separate switch under{" "}
                <strong>Config → Video calls → “Video calling enabled”</strong>.
                What you add is kept and takes effect once that switch is turned
                on.
              </Alert>
            )}

            {error && <Alert severity="error">{error}</Alert>}
            {notice && (
              <Alert severity="success" onClose={() => setNotice(null)}>
                {notice}
              </Alert>
            )}

            {issued && (
              <Alert
                severity={issued.rotated ? "warning" : "success"}
                onClose={() => setIssued(null)}
                sx={{ wordBreak: "break-all" }}
                data-testid="family-link-result"
                action={
                  <Button
                    size="small"
                    onClick={copyIssued}
                    data-testid="family-link-copy"
                  >
                    {copied ? "Copied" : "Copy"}
                  </Button>
                }
              >
                <Typography variant="body2" fontWeight={500}>
                  {issued.kind === "call"
                    ? `Call link for ${issued.contactName} - this is how they answer.`
                    : `Family portal link for ${issued.contactName} - this is how they sign in.`}
                </Typography>
                <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
                  {issued.rotated
                    ? "Their previous call link has just stopped working, so this one has to reach them. "
                    : ""}
                  {issued.emailed
                    ? "Mailed to their address. Copy it now anyway - it is not shown again."
                    : "Not mailed (no email on file, or mail is not configured). Copy it now - it is not shown again."}
                </Typography>
                <strong>{issued.url}</strong>
              </Alert>
            )}

            <Stack direction="row" justifyContent="flex-end">
              <Button
                variant="contained"
                size="small"
                startIcon={<PersonAddIcon />}
                onClick={() => {
                  setEditing(null);
                  setFormOpen(true);
                }}
                data-testid="family-add"
              >
                Add family contact
              </Button>
            </Stack>

            {contacts === null ? (
              <TableSkeleton rows={2} />
            ) : sorted.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No family contacts yet. Add the relative the resident actually
                calls - one is worth more than a complete list nobody uses.
              </Typography>
            ) : (
              <Table size="small" data-testid="family-table">
                <TableHead>
                  <TableRow>
                    <TableCell>Contact</TableCell>
                    <TableCell>Consent</TableCell>
                    <TableCell align="center">
                      <Tooltip title="Appears in the resident’s call grid, so they can call them.">
                        <span>Resident can call</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="May ring the resident’s TV.">
                        <span>May ring room</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        Phone
                        <Tooltip title="Numbers are masked. Revealing one is recorded in the compliance audit trail.">
                          <GavelIcon
                            fontSize="inherit"
                            color="action"
                            sx={{ fontSize: 14 }}
                          />
                        </Tooltip>
                      </Stack>
                    </TableCell>
                    <TableCell align="right" />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sorted.map((c) => {
                    const revoked = !!c.revokedAt;
                    const consented = !!c.consent;
                    const inert = revoked || !consented;
                    return (
                      <TableRow
                        key={c.id}
                        data-testid={`family-row-${c.id}`}
                        sx={inert ? { opacity: 0.65 } : undefined}
                      >
                        <TableCell>
                          <Stack
                            direction="row"
                            spacing={1.5}
                            alignItems="center"
                          >
                            <Avatar
                              src={c.avatarAssetId ?? undefined}
                              sx={{ width: 32, height: 32 }}
                            >
                              {c.displayName.slice(0, 1).toUpperCase()}
                            </Avatar>
                            <Box sx={{ minWidth: 0 }}>
                              <Stack
                                direction="row"
                                spacing={0.75}
                                alignItems="center"
                              >
                                <Typography variant="body2" fontWeight={500}>
                                  {c.displayName}
                                </Typography>
                                {revoked && (
                                  <Chip
                                    size="small"
                                    color="error"
                                    label="Revoked"
                                  />
                                )}
                              </Stack>
                              {c.relation && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {c.relation}
                                </Typography>
                              )}
                            </Box>
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Stack spacing={0.25}>
                            <Chip
                              size="small"
                              variant={consented ? "outlined" : "filled"}
                              color={consented ? "success" : "warning"}
                              label={consentLabel(c)}
                              data-testid={`family-consent-${c.id}`}
                            />
                            {!consented && !revoked && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                Inactive - not on the TV
                              </Typography>
                            )}
                            {consented && c.consent?.note && (
                              <Tooltip title={c.consent.note}>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  noWrap
                                  sx={{ maxWidth: 200 }}
                                >
                                  {c.consent.note}
                                </Typography>
                              </Tooltip>
                            )}
                          </Stack>
                        </TableCell>
                        <TableCell align="center">
                          <Checkbox
                            size="small"
                            checked={c.callableFromRoom}
                            disabled={inert || busyRow === c.id}
                            onChange={(e) =>
                              void toggleFlag(
                                c,
                                "callableFromRoom",
                                e.target.checked
                              )
                            }
                            inputProps={
                              {
                                "aria-label": `${c.displayName} callable from the room`,
                                "data-testid": `family-callable-${c.id}`,
                              } as React.InputHTMLAttributes<HTMLInputElement>
                            }
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Checkbox
                            size="small"
                            checked={c.canCallRoom}
                            disabled={inert || busyRow === c.id}
                            onChange={(e) =>
                              void toggleFlag(
                                c,
                                "canCallRoom",
                                e.target.checked
                              )
                            }
                            inputProps={
                              {
                                "aria-label": `${c.displayName} may ring the room`,
                                "data-testid": `family-can-ring-${c.id}`,
                              } as React.InputHTMLAttributes<HTMLInputElement>
                            }
                          />
                        </TableCell>
                        <TableCell>
                          {revealed[c.id] ? (
                            <Stack spacing={0.25}>
                              <Typography variant="body2">
                                {revealed[c.id].phoneE164 ?? "-"}
                              </Typography>
                              <Button
                                size="small"
                                variant="text"
                                sx={{
                                  alignSelf: "flex-start",
                                  minWidth: 0,
                                  px: 0,
                                }}
                                onClick={() => hide(c.id)}
                              >
                                Hide
                              </Button>
                            </Stack>
                          ) : (
                            <Stack spacing={0.25}>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {c.endpointHints?.phone ??
                                  (c.endpointKinds?.includes("phone")
                                    ? "•••"
                                    : "-")}
                              </Typography>
                              {canViewPhi &&
                                c.endpointKinds?.includes("phone") && (
                                  <Tooltip title="Decrypts the number. This action is logged for compliance.">
                                    <span>
                                      <Button
                                        size="small"
                                        variant="text"
                                        startIcon={
                                          revealing === c.id ? (
                                            <CircularProgress size={12} />
                                          ) : (
                                            <VisibilityIcon />
                                          )
                                        }
                                        onClick={() => reveal(c)}
                                        disabled={revealing === c.id}
                                        sx={{
                                          alignSelf: "flex-start",
                                          minWidth: 0,
                                          px: 0,
                                        }}
                                        data-testid={`family-reveal-${c.id}`}
                                      >
                                        Show number
                                      </Button>
                                    </span>
                                  </Tooltip>
                                )}
                            </Stack>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <Stack
                            direction="row"
                            spacing={1}
                            justifyContent="flex-end"
                          >
                            <Tooltip title="Hand this relative the link they open on their phone.">
                              <span>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={
                                    linkBusy === c.id ? (
                                      <CircularProgress size={12} />
                                    ) : (
                                      <LinkIcon />
                                    )
                                  }
                                  onClick={(e) =>
                                    setLinkMenu({
                                      anchor: e.currentTarget,
                                      contact: c,
                                    })
                                  }
                                  disabled={revoked || linkBusy === c.id}
                                  data-testid={`family-links-${c.id}`}
                                >
                                  Links
                                </Button>
                              </span>
                            </Tooltip>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<EditIcon />}
                              onClick={() => {
                                setEditing(c);
                                setFormOpen(true);
                              }}
                              disabled={revoked}
                              data-testid={`family-edit-${c.id}`}
                            >
                              Edit
                            </Button>
                            <Button
                              size="small"
                              color="error"
                              startIcon={<BlockIcon />}
                              onClick={() => {
                                setRevokeError(null);
                                setRevoking(c);
                              }}
                              disabled={revoked}
                              data-testid={`family-revoke-${c.id}`}
                            >
                              Revoke
                            </Button>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}

            <Typography variant="caption" color="text.secondary">
              Names are shown on the TV; numbers never are. Adding, editing,
              revealing a contact and issuing a link are all recorded in the
              resident’s access log.
            </Typography>
            <Typography variant="caption" color="text.secondary">
              <strong>Links</strong> gives each relative one of two different
              things. The <strong>call link</strong> (call.nenda.com) is how
              they answer when the room rings - it never expires, it stops
              working when you revoke them, and issuing a new one kills the old
              one. The <strong>portal link</strong> (family.nenda.com) is how
              they sign in to see and pay for video calling - single-use, valid
              7 days.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} data-testid="family-close">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Menu
        open={!!linkMenu}
        anchorEl={linkMenu?.anchor ?? null}
        onClose={() => setLinkMenu(null)}
      >
        <MenuItem
          onClick={() => linkMenu && void issueLink(linkMenu.contact, "call")}
          data-testid="family-issue-call-link"
        >
          Send call link (answers calls)
        </MenuItem>
        <MenuItem
          onClick={() => linkMenu && void issueLink(linkMenu.contact, "portal")}
          data-testid="family-issue-portal-link"
        >
          Send family portal link (signs in)
        </MenuItem>
      </Menu>

      <FamilyContactDialog
        open={formOpen}
        premiseId={premiseId}
        personRef={personRef}
        roomLabel={roomLabel}
        contact={editing}
        defaultTz={defaultTz}
        onClose={() => setFormOpen(false)}
        onSaved={(msg) => void afterMutation(msg)}
      />

      <DangerConfirmDialog
        open={!!revoking}
        title="Revoke this family contact?"
        entityName={revoking?.displayName ?? ""}
        confirmLabel="Revoke"
        acknowledgeLabel="I understand this contact will no longer reach the resident."
        body={
          <Typography variant="body2">
            They disappear from the resident’s call grid and can no longer ring
            the room - both directions, immediately. The row is kept so past
            calls and the consent record stay explicable; it is not deleted.
          </Typography>
        }
        errorMessage={revokeError}
        busy={revokeBusy}
        onConfirm={() => void confirmRevoke()}
        onCancel={() => setRevoking(null)}
      />
    </>
  );
}
